import agentWebSocket from "@app/config/agentWebSocket";
import { queryClient } from "@app/config/api/api";
import { agentSessionMessagesOptions, translateBackendMessages, useAgentSessionMessages } from "@app/queries/agent";
import { useAuth0 } from "@auth0/auth0-react";
import type { AgentServerMessage, ExperimentId } from "@globals";
import type { ChatMessage } from "@lib/agent/types";
import { useCallback, useEffect, useRef, useState } from "react";

let messageIdCounter = 0;
function nextId(): string {
  return `msg-${++messageIdCounter}`;
}

export function useAgentChat(experimentId: ExperimentId, teamId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const streamBufferRef = useRef("");
  const currentAssistantIdRef = useRef<string | null>(null);
  const toolCallsRef = useRef<string[]>([]);
  // Seeding the local message list happens once. Subsequent refetches (e.g. window-focus)
  // must not clobber turns that have streamed in since.
  const hasSeededRef = useRef(false);
  const experimentIdRef = useRef(experimentId);
  useEffect(() => {
    experimentIdRef.current = experimentId;
  }, [experimentId]);

  const { getAccessTokenSilently } = useAuth0();

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: historyError,
    refetch: refetchHistory
  } = useAgentSessionMessages(experimentId);

  // Seed local state from persisted history exactly once.
  //
  // Skip the setMessages call when history is empty. Effect order is child-first within a
  // commit, so on a fresh draft entered from `?query=...` ExperimentAgentChat's handoff
  // effect dispatches `send()` (which appends the user + pending-assistant messages to
  // local state) BEFORE this parent effect fires. A blanket `setMessages([...])` would
  // then overwrite that local state with the empty server history and the typed message
  // would vanish. Empty history has nothing to restore anyway.
  //
  // If the server signals `is_turn_in_progress`, a turn survived a previous WS disconnect
  // and is still running. Show the same "thinking" spinner as during live streaming so
  // the user is not left wondering why the chat appears frozen.
  useEffect(() => {
    if (hasSeededRef.current || !historyData) {
      return;
    }
    hasSeededRef.current = true;
    if (historyData.messages.length === 0) {
      if (historyData.is_turn_in_progress) {
        const pendingId = nextId();
        currentAssistantIdRef.current = pendingId;
        setMessages([{ id: pendingId, role: "assistant", content: "" }]);
        setIsStreaming(true);
      }
      return;
    }
    const seeded = translateBackendMessages(historyData.messages);
    if (historyData.is_turn_in_progress) {
      const pendingId = nextId();
      currentAssistantIdRef.current = pendingId;
      setMessages([...seeded, { id: pendingId, role: "assistant", content: "" }]);
      setIsStreaming(true);
    } else {
      setMessages(seeded);
    }
  }, [historyData]);

  const clearLocal = useCallback(() => {
    setMessages([]);
    streamBufferRef.current = "";
    currentAssistantIdRef.current = null;
    toolCallsRef.current = [];
    setIsStreaming(false);
  }, []);

  // Re-open the seed gate so the next `historyData` change rehydrates messages from the
  // server. Used by the optimistic-clear rollback path: after `clearLocal()` empties the
  // UI and the DELETE fails, the consumer calls this + `refetchHistory()` to recover.
  // We don't reset the gate inside `clearLocal` itself because on the success path that
  // would race a user typing a new message against the post-invalidate refetch.
  const allowReseed = useCallback(() => {
    hasSeededRef.current = false;
  }, []);

  // Connect on mount, reconnect when the experiment's team changes
  useEffect(() => {
    void agentWebSocket.connect(() => getAccessTokenSilently(), teamId);
    return () => agentWebSocket.close();
  }, [teamId, getAccessTokenSilently]);

  // Track connection state
  useEffect(() => agentWebSocket.onStateChange(state => setIsReady(state === "connected")), []);

  // On (re)connect, invalidate history so a turn that completed while the WS was down is
  // picked up immediately — covers the case where the agent finished before this client
  // connected and `is_turn_in_progress` was already false on the initial fetch.
  useEffect(() => {
    if (!isReady) {
      return;
    }
    void queryClient.invalidateQueries({
      queryKey: agentSessionMessagesOptions(experimentIdRef.current).queryKey
    });
  }, [isReady]);

  // Handle server messages
  useEffect(() => {
    const handleMessage = (message: AgentServerMessage) => {
      switch (message.action) {
        case "agent.response.chunk": {
          streamBufferRef.current += message.payload.message;
          const buffered = streamBufferRef.current;
          const assistantId = currentAssistantIdRef.current;
          if (assistantId) {
            setMessages(prev => prev.map(m => (m.id === assistantId ? { ...m, content: buffered } : m)));
          }
          break;
        }
        case "agent.tool_use": {
          toolCallsRef.current = [...toolCallsRef.current, message.payload.tool];
          const tools = toolCallsRef.current;
          // Insert a paragraph break so text that resumes after the tool call
          // renders as a separate markdown paragraph from the pre-tool text.
          if (streamBufferRef.current && !streamBufferRef.current.endsWith("\n\n")) {
            streamBufferRef.current += "\n\n";
          }
          const buffered = streamBufferRef.current;
          const assistantId = currentAssistantIdRef.current;
          if (assistantId) {
            setMessages(prev =>
              prev.map(m => (m.id === assistantId ? { ...m, content: buffered, toolCalls: tools } : m))
            );
          }
          break;
        }
        case "agent.response.done": {
          if (message.payload?.extras) {
            const extras = message.payload.extras;
            const assistantId = currentAssistantIdRef.current;
            if (assistantId) {
              setMessages(prev => prev.map(m => (m.id === assistantId ? { ...m, extras } : m)));
            }
          }
          setIsStreaming(false);
          currentAssistantIdRef.current = null;
          streamBufferRef.current = "";
          toolCallsRef.current = [];
          // The agent service has now persisted the AgentSession row and committed messages —
          // refresh so `agent_session_id` surfaces and the committed message count signal updates.
          void queryClient.invalidateQueries({
            queryKey: agentSessionMessagesOptions(experimentIdRef.current).queryKey
          });
          break;
        }
        case "agent.error": {
          const assistantId = currentAssistantIdRef.current;
          if (assistantId) {
            setMessages(prev =>
              prev.map(m => (m.id === assistantId ? { ...m, content: m.content || message.payload.message } : m))
            );
          } else {
            setMessages(prev => [...prev, { id: nextId(), role: "assistant", content: message.payload.message }]);
          }
          setIsStreaming(false);
          currentAssistantIdRef.current = null;
          streamBufferRef.current = "";
          toolCallsRef.current = [];
          break;
        }
        case "agent.history.updated": {
          // A detached turn (started before a WS disconnect) has just completed. Discard
          // any partial streaming state and refetch the full, persisted conversation.
          if (message.payload.experiment_id !== experimentIdRef.current) {
            break;
          }
          hasSeededRef.current = false;
          setIsStreaming(false);
          currentAssistantIdRef.current = null;
          streamBufferRef.current = "";
          toolCallsRef.current = [];
          void queryClient.invalidateQueries({
            queryKey: agentSessionMessagesOptions(experimentIdRef.current).queryKey
          });
          break;
        }
      }
    };

    return agentWebSocket.onServerMessage(handleMessage);
  }, []);

  const send = useCallback(
    (content: string): void => {
      if (!content.trim() || isStreaming || isHistoryLoading || historyError) {
        return;
      }

      const userMessage: ChatMessage = { id: nextId(), role: "user", content };
      const assistantMessage: ChatMessage = { id: nextId(), role: "assistant", content: "" };
      currentAssistantIdRef.current = assistantMessage.id;
      streamBufferRef.current = "";
      toolCallsRef.current = [];

      setMessages(prev => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);

      agentWebSocket.send({
        action: "agent.prompt",
        payload: { message: content, experiment_id: experimentId }
      });
    },
    [isStreaming, isHistoryLoading, historyError, experimentId]
  );

  const stop = useCallback(() => {
    if (!isStreaming) {
      return;
    }
    agentWebSocket.send({
      action: "agent.stop",
      payload: { experiment_id: experimentId }
    });
    // Mirror the backend cleanup locally so the partial assistant bubble disappears
    // immediately — the consumer deletes the same row server-side, so without this the
    // UI would keep showing the half-written reply until a reload refetched history.
    // The user message is preserved on both sides so the user can retry without retyping.
    const assistantId = currentAssistantIdRef.current;
    if (assistantId) {
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    }
  }, [isStreaming, experimentId]);

  const agentSessionId = historyData?.agent_session_id ?? null;

  return {
    messages,
    isStreaming,
    isReady,
    send,
    stop,
    isHistoryLoading,
    historyError,
    refetchHistory,
    clearLocal,
    allowReseed,
    // Derived from `historyData` (not from local `messages`) so callers gating on "is
    // there persisted history?" can rely on it during the same render the seed effect
    // commits — the local `messages` state lags by one render due to setState batching.
    // Uses the filtered translation so agent-only `[SYSTEM]` rows can't make a visually
    // empty chat count as restored history (e.g. suppressing the initial-query handoff).
    hasRestoredHistory: translateBackendMessages(historyData?.messages ?? []).length > 0,
    agentSessionId,
    committedMessageCount: historyData?.messages?.length ?? 0
  };
}
